import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const LOCALE = { it: "it-IT", en: "en-GB", fr: "fr-FR", ar: "ar-SA" };
const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
const estimateMs = (w) => 240 + 55 * w.length;

/** Karaoke read-aloud: speaks `text` and reports the word being spoken (boundary events, with a timing estimator fallback for engines that never fire them). */
export default function useKaraoke(text, lang, { onEnd, enabled = true } = {}) {
  const [speaking, setSpeaking] = useState(false);
  const [failed, setFailed] = useState(false);
  const [wordIndex, setWordIndex] = useState(-1);
  const words = useMemo(() => (text ? text.split(/\s+/).filter(Boolean) : []), [text]);
  const endRef = useRef(onEnd); endRef.current = onEnd;
  const timers = useRef([]);
  const gotBoundary = useRef(false);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const stop = useCallback(() => {
    clearTimers();
    if (synth) synth.cancel();
    setSpeaking(false);
    setWordIndex(-1);
  }, []);

  const speak = useCallback(() => {
    if (!synth || !text || !enabled) return;
    clearTimers();
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = LOCALE[lang] || "en-GB";
    u.rate = 0.95;
    const voice = synth.getVoices().find((v) => v.lang.replace("_", "-").toLowerCase().startsWith(u.lang.slice(0, 2)));
    if (voice) u.voice = voice;
    const starts = [];
    let pos = 0;
    for (const w of words) { pos = text.indexOf(w, pos); starts.push(pos); pos += w.length; }
    gotBoundary.current = false;
    u.onboundary = (e) => {
      if (e.name && e.name !== "word") return;
      if (!gotBoundary.current) { gotBoundary.current = true; clearTimers(); }
      const i = starts.findIndex((s, idx) => e.charIndex >= s && (idx === starts.length - 1 || e.charIndex < starts[idx + 1]));
      if (i >= 0) setWordIndex(i);
    };
    u.onstart = () => {
      setSpeaking(true); setWordIndex(0);
      // Fallback highlighter (Android Chrome rarely fires boundary events): advance by estimated word duration.
      let acc = 0;
      words.forEach((w, i) => { acc += estimateMs(w); if (i > 0) timers.current.push(setTimeout(() => { if (!gotBoundary.current) setWordIndex(i); }, acc)); });
    };
    const done = () => { clearTimers(); setSpeaking(false); setWordIndex(-1); endRef.current?.(); };
    u.onend = done;
    // Engine errors (no voice, not-allowed, synthesis-failed) → mark failed so the player falls back to the fixed timer.
    u.onerror = (e) => { clearTimers(); setSpeaking(false); setWordIndex(-1); if (e.error !== "interrupted" && e.error !== "canceled") setFailed(true); };
    // Chrome needs a beat after cancel() or the next utterance may start late / be dropped.
    timers.current.push(setTimeout(() => { synth.resume(); synth.speak(u); }, 60));
  }, [text, lang, words, enabled]);

  useEffect(() => stop, [stop, text]);

  return { words, wordIndex, speaking, speak, stop, failed, supported: !!synth };
}
